/**
 * Batch Summary Card Component
 * Quick summary view of a batch
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BatchResponse, QualityGradeConfig } from '@/types/inventory';
import { BatchStatusBadge } from './BatchStatusBadge';
import { ProductTypeDisplay } from './ProductTypeDisplay';
import { QuantityDisplay } from './QuantityDisplay';
import { ExpiryWarning } from './ExpiryWarning';
import { isExpiringSoon, isExpired } from '@/types/inventory';
import { format } from 'date-fns';
import { Calendar, MapPin, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BatchSummaryCardProps {
  batch: BatchResponse;
  onViewDetails?: () => void;
  className?: string;
}

export function BatchSummaryCard({ batch, onViewDetails, className }: BatchSummaryCardProps) {
  const gradeConfig = QualityGradeConfig[batch.qualityGrade];
  const hasExpiryWarning = isExpiringSoon(batch.expiryDate) || isExpired(batch.expiryDate);

  return (
    <Card className={cn('hover:shadow-lg transition-shadow', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg font-bold">{batch.batchNumber}</CardTitle>
            <div className="mt-2">
              <ProductTypeDisplay productType={batch.productType} variant="inline" />
            </div>
          </div>
          <BatchStatusBadge status={batch.batchStatus} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quantity Display */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <div className="text-sm text-gray-500 mb-1">Available</div>
            <QuantityDisplay
              quantity={batch.availableQuantity}
              unit={batch.quantityUnit}
              variant="default"
            />
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Total</div>
            <QuantityDisplay
              quantity={batch.currentQuantity}
              unit={batch.quantityUnit}
              variant="default"
            />
          </div>
        </div>

        {/* Quality Grade */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Quality</span>
          <Badge className={cn(gradeConfig.bgColor, gradeConfig.color, 'border-none')}>
            Grade {batch.qualityGrade} • {batch.purityPercentage.toFixed(1)}%
          </Badge>
        </div>

        {/* Dates */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Produced: {format(new Date(batch.productionDate), 'MMM dd, yyyy')}</span>
          </div>
          <div className={cn(
            'flex items-center gap-2',
            hasExpiryWarning ? 'text-orange-600 font-medium' : 'text-gray-600'
          )}>
            <Calendar className="h-4 w-4" />
            <span>Expires: {format(new Date(batch.expiryDate), 'MMM dd, yyyy')}</span>
          </div>
        </div>

        {/* Storage Location */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="h-4 w-4" />
          <span className="truncate">{batch.storageLocation}</span>
        </div>

        {/* Expiry Warning */}
        {hasExpiryWarning && (
          <ExpiryWarning expiryDate={batch.expiryDate} />
        )}

        {/* View Details Button */}
        {onViewDetails && (
          <Button
            variant="outline"
            className="w-full"
            onClick={onViewDetails}
          >
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
